/**
 * Template Management — full CRUD with worksheet-sourced creation.
 */
import { templateService } from '../services/api';
import { sanitize } from '../services/utils';

let _container: HTMLElement;
let _templates: any[] = [];
let _filter = '';

const labelStyle = 'font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:var(--colorTextLight);margin-bottom:6px';

export async function renderTemplateMgmt(container: HTMLElement): Promise<void> {
  _container = container;
  const { templates } = await templateService.list();
  _templates = templates;
  render();
}

function render(): void {
  const q = _filter.toLowerCase();
  const filtered = q ? _templates.filter((t) =>
    t.label.toLowerCase().includes(q) || t.template_id.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q)
  ) : _templates;

  const toolbar = document.getElementById('admin-sub-toolbar');
  if (toolbar) {
    toolbar.innerHTML = `
      <input id="tpl-filter" type="text" placeholder="Filter..." value="${sanitize(_filter)}"
        style="padding:4px 12px;border:1px solid var(--colorBorderInput);border-radius:9999px;font-size:11px;background:var(--colorBgInput);color:var(--colorText);width:160px;box-sizing:border-box">
      <button id="create-from-ws-btn" style="padding:4px 12px;border-radius:9999px;font-size:11px;font-weight:500;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">+ From Worksheet</button>
    `;
  }

  _container.innerHTML = `
    ${filtered.length === 0
      ? `<div style="text-align:center;padding:32px 0;color:var(--colorTextLight);font-size:13px">${q ? 'No templates match your filter.' : 'No templates found.'}</div>`
      : `<div style="display:grid;gap:6px">${filtered.map((t) => renderTemplateRow(t)).join('')}</div>`}
  `;

  document.getElementById('tpl-filter')?.addEventListener('input', (e) => {
    _filter = (e.target as HTMLInputElement).value;
    render();
    const input = document.getElementById('tpl-filter') as HTMLInputElement;
    if (input) { input.focus(); input.selectionStart = input.selectionEnd = input.value.length; }
  });

  document.getElementById('create-from-ws-btn')?.addEventListener('click', showCreateFromWorksheetModal);

  document.querySelectorAll('[data-edit-tpl]').forEach((el) => {
    el.addEventListener('click', () => {
      const id = (el as HTMLElement).dataset.editTpl!;
      const tpl = _templates.find((t) => t.template_id === id);
      if (tpl) showEditTemplateModal(tpl);
    });
  });
}

function renderTemplateRow(t: any): string {
  return `
    <div data-edit-tpl="${t.template_id}" style="display:flex;align-items:center;gap:12px;padding:12px 16px;border-radius:8px;background:var(--colorBgElevated);border:1px solid var(--colorBorder);cursor:pointer;transition:border-color .15s" onmouseover="this.style.borderColor='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)'">
      <span style="width:36px;height:36px;display:flex;align-items:center;justify-content:center;border-radius:6px;font-size:10px;font-weight:700;background:var(--colorBgCard);border:1px solid var(--colorBorder)">${sanitize(t.abbr)}</span>
      <div style="flex:1;min-width:0">
        <div style="font-size:13px;font-weight:500;color:var(--colorTextHeading)">${sanitize(t.label)}</div>
        <div style="font-size:10px;color:var(--colorTextMuted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${sanitize(t.description)}</div>
      </div>
      <div style="display:flex;align-items:center;gap:8px;flex-shrink:0">
        <span style="font-size:10px;padding:2px 8px;border-radius:4px;background:var(--colorBgCard);border:1px solid var(--colorBorder);color:var(--colorTextMuted)">v${sanitize(t.version)}</span>
        <span style="font-size:9px;color:var(--colorTextLight)">${t.actors?.length || 0} actors · ${t.controls?.length || 0} controls</span>
        ${t.is_default ? '<span style="font-size:8px;padding:1px 4px;border-radius:3px;background:var(--colorSuccess);color:#fff">Default</span>' : ''}
      </div>
    </div>
  `;
}

// ─── Edit Template Modal ───────────────────────────────

function showEditTemplateModal(t: any): void {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  const modal = document.createElement('div');
  modal.className = 'modal-box';
  modal.style.maxWidth = '600px';
  modal.innerHTML = `
    <form id="tpl-edit-form">
      <div style="display:flex;align-items:flex-start;gap:14px;padding:16px;border-radius:8px;background:var(--colorBgElevated);border:1px solid var(--colorBorder);margin-bottom:20px">
        <input id="te-abbr" value="${sanitize(t.abbr)}" maxlength="5" style="width:48px;height:48px;text-align:center;font-size:13px;font-weight:700;border-radius:8px;border:1px solid var(--colorBorder);background:var(--colorBgCard);color:var(--colorTextHeading)">
        <div style="flex:1">
          <div style="font-size:9px;color:var(--colorTextLight);font-family:monospace;margin-bottom:4px">${sanitize(t.template_id)}</div>
          <input id="te-label" value="${sanitize(t.label)}" required style="font-size:16px;font-weight:500;color:var(--colorTextHeading);border:none;border-bottom:1px solid var(--colorBorderInput);background:transparent;width:100%;padding:4px 0;margin-bottom:6px;outline:none">
          <input id="te-desc" value="${sanitize(t.description)}" placeholder="Description..." style="font-size:12px;color:var(--colorTextMuted);border:none;border-bottom:1px solid transparent;background:transparent;width:100%;padding:4px 0;outline:none">
        </div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:18px">
        <div>
          <div style="${labelStyle}">Actors (${t.actors?.length || 0})</div>
          <div style="max-height:150px;overflow-y:auto;display:flex;flex-wrap:wrap;gap:4px">
            ${(t.actors || []).map((a: string) => `<span style="font-size:10px;padding:2px 8px;border-radius:4px;background:var(--colorBgCard);border:1px solid var(--colorBorder);color:var(--colorTextMuted)">${sanitize(a)}</span>`).join('')}
          </div>
        </div>
        <div>
          <div style="${labelStyle}">Controls (${t.controls?.length || 0})</div>
          <div style="max-height:150px;overflow-y:auto;display:flex;flex-wrap:wrap;gap:4px">
            ${(t.controls || []).map((c: string) => `<span style="font-size:10px;padding:2px 8px;border-radius:4px;background:var(--colorBgCard);border:1px solid var(--colorBorder);color:var(--colorTextMuted)">${sanitize(c)}</span>`).join('')}
          </div>
        </div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:18px;font-size:11px">
        <div>
          <div style="${labelStyle}">Version</div>
          <div style="color:var(--colorText)">v${sanitize(t.version)} ${t.is_default ? '(default)' : ''}</div>
        </div>
        <div>
          <div style="${labelStyle}">Created</div>
          <div style="color:var(--colorText)">${t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}</div>
        </div>
      </div>

      <!-- Publish new version -->
      <details style="margin-bottom:18px">
        <summary style="font-size:11px;color:var(--colorAccent);cursor:pointer;user-select:none">Publish New Version</summary>
        <div style="padding:12px 0">
          <div style="display:flex;gap:8px;margin-bottom:8px">
            <label style="display:flex;align-items:center;gap:4px;font-size:11px;color:var(--colorTextMuted);cursor:pointer">
              <input type="radio" name="bump" value="patch" checked> Patch
            </label>
            <label style="display:flex;align-items:center;gap:4px;font-size:11px;color:var(--colorTextMuted);cursor:pointer">
              <input type="radio" name="bump" value="minor"> Minor
            </label>
            <label style="display:flex;align-items:center;gap:4px;font-size:11px;color:var(--colorTextMuted);cursor:pointer">
              <input type="radio" name="bump" value="major"> Major
            </label>
          </div>
          <input id="te-migration-notes" placeholder="Migration notes..." style="width:100%;padding:6px 10px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:12px;background:var(--colorBgInput);color:var(--colorText);box-sizing:border-box;margin-bottom:8px">
          <button type="button" id="te-publish" style="padding:6px 14px;border-radius:6px;font-size:11px;font-weight:500;cursor:pointer;border:none;background:var(--colorSuccess);color:#fff">Publish Version</button>
        </div>
      </details>

      <!-- Delete -->
      ${!t.is_default ? `
      <details style="margin-bottom:18px">
        <summary style="font-size:11px;color:var(--colorError);cursor:pointer;user-select:none">Delete Template</summary>
        <div style="padding:12px 0">
          <p style="font-size:11px;color:var(--colorTextMuted);margin-bottom:8px">This will delete all versions of this template. Worksheets created from it will retain their data.</p>
          <button type="button" id="te-delete" style="padding:6px 14px;border-radius:6px;font-size:11px;font-weight:600;cursor:pointer;border:none;background:var(--colorError);color:#fff">Delete All Versions</button>
        </div>
      </details>
      ` : ''}

      <div style="display:flex;gap:8px">
        <button type="button" onclick="this.closest('.modal-overlay').remove()" style="flex:1;padding:12px;border-radius:8px;font-size:14px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Cancel</button>
        <button type="submit" style="flex:1;padding:12px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">Save</button>
      </div>
    </form>
  `;
  overlay.appendChild(modal);
  document.body.appendChild(overlay);

  // Save metadata
  document.getElementById('tpl-edit-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    await templateService.update(t.template_id, {
      label: (document.getElementById('te-label') as HTMLInputElement).value.trim(),
      abbr: (document.getElementById('te-abbr') as HTMLInputElement).value.trim(),
      description: (document.getElementById('te-desc') as HTMLInputElement).value.trim(),
    } as any);
    overlay.remove();
    const { templates } = await templateService.list();
    _templates = templates;
    render();
  });

  // Publish new version
  document.getElementById('te-publish')?.addEventListener('click', async () => {
    const bump = (modal.querySelector('input[name="bump"]:checked') as HTMLInputElement)?.value || 'patch';
    const notes = (document.getElementById('te-migration-notes') as HTMLInputElement).value.trim();
    await templateService.publishVersion(t.template_id, { bump: bump as any, migration_notes: notes });
    overlay.remove();
    const { templates } = await templateService.list();
    _templates = templates;
    render();
  });

  // Delete
  document.getElementById('te-delete')?.addEventListener('click', async () => {
    await (await fetch(`/api/v1/templates/${t.template_id}`, { method: 'DELETE', credentials: 'include' }));
    overlay.remove();
    const { templates } = await templateService.list();
    _templates = templates;
    render();
  });
}

// ─── Create from Worksheet Modal ───────────────────────

async function showCreateFromWorksheetModal(): Promise<void> {
  // Fetch all eligible worksheets
  const res = await fetch('/api/v1/templates/worksheets/all', { credentials: 'include' });
  const { worksheets } = await res.json();

  let selectedWs: any = null;
  let omitActors = new Set<string>();
  let omitControls = new Set<string>();

  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';
  overlay.onclick = (e) => { if (e.target === overlay) overlay.remove(); };
  const modal = document.createElement('div');
  modal.className = 'modal-box';
  modal.style.maxWidth = '700px';
  modal.style.maxHeight = '85vh';
  modal.style.overflow = 'auto';

  function renderModalContent(): void {
    if (!selectedWs) {
      // Step 1: Pick a worksheet
      modal.innerHTML = `
        <h3 style="font-size:16px;font-weight:600;color:var(--colorTextHeading);margin-bottom:16px">Create Template from Worksheet</h3>
        <p style="font-size:12px;color:var(--colorTextMuted);margin-bottom:16px">Select a worksheet to use as the template baseline.</p>
        <div style="display:grid;gap:6px;max-height:400px;overflow-y:auto">
          ${worksheets.map((w: any) => `
            <div data-pick-ws="${w._id}" style="display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:6px;background:var(--colorBgElevated);border:1px solid var(--colorBorder);cursor:pointer;transition:border-color .15s" onmouseover="this.style.borderColor='var(--colorAccent)'" onmouseout="this.style.borderColor='var(--colorBorder)'">
              <div style="flex:1;min-width:0">
                <div style="font-size:12px;font-weight:500;color:var(--colorTextHeading)">${sanitize(w.name)}</div>
                <div style="font-size:10px;color:var(--colorTextMuted)">${sanitize(w.folder_name)}${w.stage ? ' · ' + sanitize(w.stage) : ''}</div>
              </div>
              <span style="font-size:9px;color:var(--colorTextLight)">${w.actor_count} actors · ${w.control_ids.length} controls</span>
            </div>
          `).join('')}
          ${worksheets.length === 0 ? '<div style="text-align:center;padding:24px;font-size:12px;color:var(--colorTextLight)">No eligible worksheets found. Worksheets must be in-progress, in-review, or complete.</div>' : ''}
        </div>
        <button type="button" onclick="this.closest('.modal-overlay').remove()" style="margin-top:12px;width:100%;padding:10px;border-radius:8px;font-size:13px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Cancel</button>
      `;
      modal.querySelectorAll('[data-pick-ws]').forEach((el) => {
        el.addEventListener('click', () => {
          const id = (el as HTMLElement).dataset.pickWs!;
          selectedWs = worksheets.find((w: any) => w._id === id);
          omitActors = new Set();
          omitControls = new Set();
          renderModalContent();
        });
      });
    } else {
      // Step 2: Configure — choose what to include/omit
      const actorTypes = [...new Set(selectedWs.actor_types)] as string[];
      const controlIds = selectedWs.control_ids as string[];

      modal.innerHTML = `
        <h3 style="font-size:16px;font-weight:600;color:var(--colorTextHeading);margin-bottom:4px">Configure Template</h3>
        <p style="font-size:12px;color:var(--colorTextMuted);margin-bottom:16px">Based on: <strong>${sanitize(selectedWs.name)}</strong>. Uncheck items to omit from the template.</p>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:18px">
          <div>
            <input id="ct-id" placeholder="template-id (slug)" required style="width:100%;padding:8px 10px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:12px;background:var(--colorBgInput);color:var(--colorText);box-sizing:border-box;font-family:monospace;margin-bottom:8px">
            <input id="ct-label" placeholder="Template Name" required style="width:100%;padding:8px 10px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:14px;background:var(--colorBgInput);color:var(--colorText);box-sizing:border-box;margin-bottom:8px">
          </div>
          <div>
            <input id="ct-abbr" placeholder="Abbreviation (e.g., AGT)" maxlength="5" style="width:100%;padding:8px 10px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:12px;background:var(--colorBgInput);color:var(--colorText);box-sizing:border-box;margin-bottom:8px">
            <input id="ct-desc" placeholder="Description" style="width:100%;padding:8px 10px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:12px;background:var(--colorBgInput);color:var(--colorText);box-sizing:border-box">
          </div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:18px">
          <div>
            <div style="${labelStyle}">Actors (${actorTypes.length})</div>
            <div style="max-height:200px;overflow-y:auto;display:flex;flex-direction:column;gap:3px">
              ${actorTypes.map((a) => `
                <label style="display:flex;align-items:center;gap:6px;font-size:11px;color:var(--colorText);cursor:pointer;padding:3px 0">
                  <input type="checkbox" data-omit-actor="${a}" checked>
                  ${sanitize(a)}
                </label>
              `).join('')}
            </div>
          </div>
          <div>
            <div style="${labelStyle}">Controls (${controlIds.length})</div>
            <div style="max-height:200px;overflow-y:auto;display:flex;flex-direction:column;gap:3px">
              ${controlIds.map((c) => `
                <label style="display:flex;align-items:center;gap:6px;font-size:11px;color:var(--colorText);cursor:pointer;padding:3px 0">
                  <input type="checkbox" data-omit-control="${c}" checked>
                  ${sanitize(c)}
                </label>
              `).join('')}
            </div>
          </div>
        </div>

        <div style="display:flex;gap:8px">
          <button type="button" id="ct-back" style="padding:12px;border-radius:8px;font-size:13px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Back</button>
          <button type="button" onclick="this.closest('.modal-overlay').remove()" style="flex:1;padding:12px;border-radius:8px;font-size:13px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgElevated);color:var(--colorText)">Cancel</button>
          <button type="button" id="ct-create" style="flex:1;padding:12px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">Create Template</button>
        </div>
      `;

      modal.querySelector('#ct-back')?.addEventListener('click', () => {
        selectedWs = null;
        renderModalContent();
      });

      modal.querySelector('#ct-create')?.addEventListener('click', async () => {
        // Collect omitted items (unchecked checkboxes)
        const omitA: string[] = [];
        modal.querySelectorAll('[data-omit-actor]').forEach((el) => {
          if (!(el as HTMLInputElement).checked) omitA.push((el as HTMLInputElement).dataset.omitActor!);
        });
        const omitC: string[] = [];
        modal.querySelectorAll('[data-omit-control]').forEach((el) => {
          if (!(el as HTMLInputElement).checked) omitC.push((el as HTMLInputElement).dataset.omitControl!);
        });

        try {
          const res = await fetch('/api/v1/templates/from-worksheet', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              worksheet_id: selectedWs._id,
              template_id: (document.getElementById('ct-id') as HTMLInputElement).value.trim(),
              label: (document.getElementById('ct-label') as HTMLInputElement).value.trim(),
              abbr: (document.getElementById('ct-abbr') as HTMLInputElement).value.trim(),
              description: (document.getElementById('ct-desc') as HTMLInputElement).value.trim(),
              omit_actors: omitA,
              omit_controls: omitC,
            }),
          });
          if (!res.ok) {
            const err = await res.json();
            alert(err?.error?.message || 'Failed to create template');
            return;
          }
          overlay.remove();
          const { templates } = await templateService.list();
          _templates = templates;
          render();
        } catch (err: any) {
          alert(err.message || 'Failed');
        }
      });
    }
  }

  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  renderModalContent();
}
